import { DEFAULT_PAGE_SIZE } from 'components/common/DataGridDevEx/DataGrid.constants';
import {
  fetchPost,
  objectify,
  fetchGet,
  Inline
} from '../utils';
import {
  APIMethod,
  Payload,
  prepareUrl,
  useFetch,
  usePost,
  useUpdate,
  useFetchMutation
} from 'api/reactQuery/queryService';
import { APIRoutes } from './constants';
import { useQueryClient } from '@tanstack/react-query';
import { IBatchInfo } from 'components/common/DataGridDevEx/DataGrid.properties';
import { IFilterRequest } from 'api/swaggerTypes';

export const search = (query) => {
  const { SearchText, Sort, BatchPage, BatchSize = DEFAULT_PAGE_SIZE } = query;

  return fetchPost('ApplicationDashboardMaintenance/Dashboard/Search', { SearchText, Sort, BatchPage, BatchSize })
    .then((response) => ({
      records: response.Dashboards.map((val) => ({ inlineObject: objectify(val), schema: val })),
      currPage: response.BatchInformation && response.BatchInformation.BatchPage,
      nextPage: response.BatchInformation && !response.BatchInformation.EndOfData,
      prevPage: response.BatchInformation && response.BatchInformation.BatchPage > 1
    }));
};

export const searchById = (DashboardId) => {
  if (DashboardId > 1) {
    const apiCall = fetchGet(`ApplicationDashboardMaintenance/Dashboard/${DashboardId}`, {}, 'Inline')
      .then((response) => ({
        records: [{ inlineObject: objectify(response.DashboardDetail), schema: response.DashboardDetail }],
        currPage: 1,
        nextPage: false,
        prevPage: false
      }));

    return apiCall;
  }

  return [];
};

const fetchDasboardDetailsResponseParser = (response): Promise<any> => {
  return {
    ...response,
    DashboardDetail: { inlineObject: objectify(response.DashboardDetail), schema: response.DashboardDetail },
    DashboardSummary: { inlineObject: objectify(response.DashboardSummary), schema: response.DashboardSummary }
  };
};

export const useFetchDashboardDetails = (DashboardId: number) => {
  return useFetch<any>(
    {
      url: APIRoutes.dashboardMaintenanceEnquiry.dashboardDetails.retrieve,
      urlParams: { DashboardId },
      queryConfig: { enabled: !!DashboardId },
      options: { inline: true, responseParser: fetchDasboardDetailsResponseParser }
    }
  );
};

const saveDashboardDetailsResponseParser = (response): Promise<any> => {
  return {
    ...response,
    DashboardDetail: { inlineObject: objectify(response.DashboardDetail), schema: response.DashboardDetail },
    DashboardSummary: { inlineObject: objectify(response.DashboardSummary), schema: response.DashboardSummary }
  };
};

export const useCreateDashboardDetails = () => {

  return usePost<Payload<{ DashboardId: number; query: any }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.dashboardDetails.create, null, { inline: true, responseParser: saveDashboardDetailsResponseParser });
};

export const useUpdateDashboardDetails = () => {

  return useUpdate<Payload<{ DashboardId: number; query: any }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.dashboardDetails.update, null, { inline: true, responseParser: saveDashboardDetailsResponseParser });
};

export const useSetDMEDashboardDetailsQueryData = () => {
  const queryClient = useQueryClient();

  return (DashboardId: number, dataToUpdate: any) => {
    const finalUrl = prepareUrl(APIRoutes.dashboardMaintenanceEnquiry.dashboardDetails.retrieve, { DashboardId }, null, true);
    queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
  };
};

export const useDeleteDashboardDetails = () => {

  return usePost<Payload<{ DashboardId: number; DeleteDashboard?: boolean }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.dashboardDetails.delete, null, { inline: false });
};

const fetchDasboardTypeQuickLinksResponseParser = (response): { QuickLinks: any[]; Schema?: any } => {
  if (!Array.isArray(response?.QuickLinks)) return { QuickLinks: [] };

  const QuickLinks = response?.QuickLinks?.map((item) => {
    const parsedItem: Record<string, any> = {};
    Object.entries(item).forEach(([key, value]) => {
      parsedItem[key] = (value as any)?.Value;
    });

    return parsedItem;
  });

  return { QuickLinks, Schema: response?.QuickLinks };
};

export const useFetchDashboardTypeQuickLinks = () =>
  useFetchMutation<Payload<{ DashboardType?: string }>, any>(
    APIRoutes.dashboardMaintenanceEnquiry.quickLinks.dashboardType.retrieve,
    null,
    {
      inline: true,
      responseParser: fetchDasboardTypeQuickLinksResponseParser
    }
  );

const dashboardQuickLinksResponseParser = (response): Promise<any> => {
  const { DashboardQuickLinks } = response;

  return {
    ...response,
    DashboardQuickLinks: DashboardQuickLinks.map((o) => objectify(o as Inline<any>))
  };
};

export const useFetchDashboardQuickLinks = (DashboardId: number | string) => {
  return useFetch<any>(
    {
      url: APIRoutes.dashboardMaintenanceEnquiry.quickLinks.retrieve,
      urlParams: { DashboardId },
      queryConfig: { enabled: (DashboardId !== -1 && !!DashboardId) },
      options: { inline: true, responseParser: dashboardQuickLinksResponseParser }
    }
  );
};

export const useSetDashboardQuickLinksQueryData = () => {
  const queryClient = useQueryClient();

  return (DashboardId: number, dataToUpdate: any) => {
    const finalUrl = prepareUrl(APIRoutes.dashboardMaintenanceEnquiry.quickLinks.retrieve, { DashboardId }, null, true);
    queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
  };
};

export const useSaveDashboardQuickLinks = () => {

  return usePost<Payload<{ DashboardId: number; DashboardQuickLinks: any; EnableQuickLinks: boolean }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.quickLinks.save, null, { inline: true, responseParser: dashboardQuickLinksResponseParser });
};

const addWidgetResponseParser = (response): Promise<any> => {
  return {
    ...response
  };
};

export const useAddWidget = () => {

  return usePost<Payload<{ DashboardId: string | number; WidgetId: number; query?: any }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.widgets.create, null, { inline: true, responseParser: addWidgetResponseParser });
};

const dashboardLayoutWidgetsResponseParser = (response): Promise<any> => {
  return {
    ...response,
    DashboardWidgets: response.DashboardWidgets?.map((o) => objectify(o as Inline<any>)),
  };
};

export const useFetchDashboardWidgets = (DashboardId: number) => {
  return useFetch<any>(
    {
      url: APIRoutes.dashboardMaintenanceEnquiry.widgets.retrieve,
      urlParams: { DashboardId },
      queryConfig: { enabled: !!DashboardId },
      options: { inline: true, responseParser: dashboardLayoutWidgetsResponseParser }
    }
  );
};

export const useSetDashboardLayoutWidgetsQueryData = () => {
  const queryClient = useQueryClient();

  return (DashboardId: number, dataToUpdate: any) => {
    const finalUrl = prepareUrl(APIRoutes.dashboardMaintenanceEnquiry.widgets.retrieve, { DashboardId }, null, true);
    queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
  };
};

export const useSaveDashboardWidgetsOrder = () => {

  return usePost<Payload<{ DashboardId: number; DashboardWidgets: any[]; query?: any }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.widgets.reorder, null, { inline: true, responseParser: dashboardLayoutWidgetsResponseParser });
};

export const useDeleteDashboardWidget = () => {

  return usePost<Payload<{ DashboardId: number; DashboardWidgetId: number }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.widgets.delete, null, { inline: true, responseParser: dashboardLayoutWidgetsResponseParser });
};

const fetchDashboardWidgetConfigurationFieldsResponseParser = (response): { WidgetFields: any[]; Schema?: any } => {
  if (!Array.isArray(response?.WidgetFields)) return { WidgetFields: [] };

  const WidgetFields = response?.WidgetFields?.map((item) => {
    const parsedItem: Record<string, any> = {};
    Object.entries(item).forEach(([key, value]) => {
      parsedItem[key] = (value as any)?.Value;
    });

    return parsedItem;
  });

  return { WidgetFields, Schema: response?.WidgetFields };
};

export const useFetchDashboardWidgetConfigurationFields = () =>
  useFetchMutation<Payload<{ WidgetId?: number | string }>, any>(
    APIRoutes.dashboardMaintenanceEnquiry.widgets.configuration.retrieve,
    null,
    {
      inline: true,
      responseParser: fetchDashboardWidgetConfigurationFieldsResponseParser
    }
  );

const dashboardWidgetFieldListResponseParser = (response): Promise<any> => {
  const { DashboardWidgetFields } = response;

  return {
    ...response,
    DashboardWidgetFields: DashboardWidgetFields.map((o) => objectify(o as Inline<any>))
  };
};

export const useFetchDashboardWidgetFieldList = (WidgetId: string | number) => {
  return useFetch<any>(
    {
      url: APIRoutes.dashboardMaintenanceEnquiry.widgets.configuration.fieldList.retrieve,
      urlParams: { WidgetId },
      queryConfig: { enabled: !!WidgetId },
      options: { inline: true, responseParser: dashboardWidgetFieldListResponseParser }
    }
  );
};

export const useSetDashboardWidgetFieldListQueryData = () => {
  const queryClient = useQueryClient();

  return (WidgetId: number, dataToUpdate: any) => {
    const finalUrl = prepareUrl(APIRoutes.dashboardMaintenanceEnquiry.widgets.configuration.fieldList.retrieve, { WidgetId }, null, true);
    queryClient.setQueryData([APIMethod.GET, finalUrl], dataToUpdate);
  };
};

const dashboardWidgetConfigurationResponseParser = (response): Promise<any> => {

  return response;
};

export const useSaveDashboardWidgetConfiguration = () => {

  return usePost<Payload<{ DashboardWidgetId: number; DashboardWidgetFields: any[]; Title: string; MaximumDisplayRows?: number | string }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.widgets.configuration.save, null, { inline: true, responseParser: dashboardWidgetConfigurationResponseParser });
};

const enquiryProgramDashboardWidgetsResponseParser = async (response): Promise<any> => {
  const dashWidgets = response?.Dashboard?.DashWidgets ?? [];

  return {
    ...response,
    Dashboard: {
      ...response.Dashboard,
      DashWidgets: dashWidgets.map((o) => objectify(o as Inline<any>))
    }
  };
};

export const useFetchEnquiryProgramDashboardWidgets = () =>
  useFetchMutation<Payload<{ programId?: string }>, any>(
    APIRoutes.dashboardMaintenanceEnquiry.enquiryProgram.widgets.retrieve,
    null,
    {
      inline: true,
      responseParser: enquiryProgramDashboardWidgetsResponseParser
    }
  );

const genericResponseParser = async (response): Promise<any> => {

  return {
    ...response,
    GridDataSchema: response,
    GridData: response?.GridData.map((o) => objectify(o as Inline<any>))
  };
};

export const useRetrieveProgramGridLines = (program) =>
  usePost<Payload<{ DashboardWidgetId: number; CustomerId?: number | string }, IBatchInfo>, any>(
    APIRoutes.dashboardMaintenanceEnquiry[program?.name]?.dashboard[program.grid]?.retrieve,
    null,
    {
      inline: true,
      responseParser: genericResponseParser
    }
  );

export const useFetchDataGridFilters = <R extends object>() =>
  usePost<Payload<IFilterRequest>, R>('Filters/GetFilterOptionsList', null, null);

export const useFetchFieldsDetails = (DashboardId: number, keyId: string) => {
  return useFetch<any>(
    {
      url: APIRoutes.dashboardMaintenanceEnquiry.fields.retrieve,
      urlParams: { DashboardId },
      urlQueryParams: { keyId },
      queryConfig: { enabled: Boolean(DashboardId) && Boolean(keyId) },
      options: { inline: true, responseParser: (response) => response }
    }
  );
};

export const useAlertDetails = (DashboardId: number, keyId: string) => {
  return useFetch<any>(
    {
      url: APIRoutes.dashboardMaintenanceEnquiry.alerts.retrieve,
      urlParams: { DashboardId },
      urlQueryParams: { keyId },
      queryConfig: { enabled: Boolean(DashboardId) && Boolean(keyId) },
      options: { inline: true, responseParser: (response) => response }
    }
  );
};

const hyperlinksResponseParser = async (response): Promise<any> => {

  return response;
};

export const useRetrieveColumnHyperlink = () => {

  return usePost<Payload<{ WidgetId: number; FieldName?: string; Key1: string; Key2?: string }>, any>
    (APIRoutes.dashboardMaintenanceEnquiry.hyperlinks.retrieve, null, { inline: true, responseParser: hyperlinksResponseParser });
};

export const useGetDMEDashboardDetailsQueryData = () => {
  const queryClient = useQueryClient();

  return (DashboardId: number) => {
    const finalUrl = prepareUrl(
      APIRoutes.dashboardMaintenanceEnquiry.dashboardDetails.retrieve,
      { DashboardId },
      null,
      true
    );

    return queryClient.getQueryData([APIMethod.GET, finalUrl]);
  };
};
